import { useDispatch, useSelector } from "react-redux";
import { MENU_PAGES } from "../../config/menuPages";
import { useLocation, useNavigate } from "react-router-dom";
import { Fragment, useEffect, useState } from "react";
import { useBizTypeTranslation } from "hooks/useBizTypeTranslation";
import Dialog from "../profile/Dialog";
import NullCheck from "../../components/NullCheck";
import useCheckData from "../../hooks/CheckData";
import {
    Box,
    Drawer,
    Toolbar,
    List,
    ListItemButton,
    ListItemIcon,
    Stack,
    Divider,
    Typography,
    Tooltip,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { ArrowBackOutlined } from "@mui/icons-material";
import { setOpenSupport } from "store/sysSlice";
import Footer from "./Footer";
import http from "../../http"
import { apiUrl } from "config/settings";
import { setBiz } from "store/bizSlice";

const Menu = ({ openMenu, setOpenMenu }) => {
    const theme = useTheme();
    const dispatch = useDispatch()
    const { hasUserPlugin } = useCheckData();
    const { bt } = useBizTypeTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const user = useSelector((state) => state.userSettings?.user);
    const userRole = useSelector((state) => state.userSettings?.user?.userRole);
    const lang = useSelector((state) => state?.userSettings?.selectedLang);
    const selectedPlugin = useSelector((state) => state?.userSettings?.selectedPlugin);
    const biz = useSelector((state) => state?.biz?.data);
    const [locationPage, setLocationPage] = useState(null);
    const [openProfile, setOpenProfile] = useState(false);
    const [list, setList] = useState([]);
    const drawerWidth = openMenu ? 220 : 60;

    useEffect(() => {
        const newMenuList = MENU_PAGES.filter((btn) => {
            const inSelectedProduct =
                !btn.prods || btn.prods.length === 0 || btn.prods.includes(selectedPlugin);

            const hasRequiredPlugins =
                !btn.plugins || btn.plugins.length === 0 || hasUserPlugin(btn.plugins);

            let roleGroups = [];

            if (!btn.showRoles || btn.showRoles.length === 0) {
                roleGroups = [];
            } else if (Array.isArray(btn.showRoles[0])) {
                roleGroups = btn.showRoles;
            }

            const allowedRole =
                roleGroups.length === 0 ||
                roleGroups.some((group) => group.includes(userRole));

            return inSelectedProduct && hasRequiredPlugins && allowedRole;
        });

        setList(newMenuList);
    }, [userRole, selectedPlugin]);


    useEffect(() => {
        setLocationPage(location.pathname)
    }, [location]);

    const onClick = (btn) => {
        if (btn.path === "/support") dispatch(setOpenSupport(true))
        else if (btn.path === "/init") systemReload();
        else navigate(`${btn.path}`);
        if (isMobile) setOpenMenu(false);
    }

    const systemReload = async () => {
        try {
            const res = await http.get(`${apiUrl}/business/settings/${biz?.id}`)
            const bizData = res?.data;
            localStorage.setItem("biz", JSON.stringify(bizData));
            dispatch(setBiz(bizData));
            window.location.reload()
        } catch (error) {
            console.log("error", error);
        }
    }

    return (
        <>
            <Drawer
                onClose={() => setOpenMenu(false)}
                open={isMobile ? openMenu : true}
                anchor='bottom'
                variant={isMobile ? "temporary" : "permanent"}
                PaperProps={{ sx: { height: isMobile ? "99vh" : "calc(100vh - 4rem)" } }}
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    [`& .MuiDrawer-paper`]: {
                        right: isMobile ? null : lang?.dir === "rtl" ? 0 : "auto",
                        left: isMobile ? null : lang?.dir === "rtl" ? 0 : "auto",
                        width: drawerWidth,
                        bgcolor: 'bg.main',
                        border: 0
                    },
                }}
                SlideProps={{ direction: lang?.dir === "rtl" ? "left" : "right" }}
            // SlideProps={{ direction: "left" }}
            >
                {isMobile ? <Toolbar /> : null}

                <Stack
                    width="100%"
                    direction="row"
                    padding={1}
                    justifyContent={isMobile || !openMenu ? "center" : "start"}
                    alignItems='center'
                >
                    <NullCheck valueForCheck={isMobile && openMenu}>
                        <Tooltip title={bt("PRIVATE_AREA")}>
                            <Typography
                                fontSize={16}
                                noWrap
                                fontWeight='bold'
                                sx={{
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                                onClick={() => setOpenProfile(true)}
                            >
                                {`${user?.fn} ${user?.ln} - ${biz?.name}`}
                            </Typography>
                        </Tooltip>
                    </NullCheck>

                    <NullCheck valueForCheck={!isMobile}>
                        <Stack
                            onClick={() => setOpenMenu((open) => !open)}
                            sx={{ cursor: "pointer" }}
                            alignItems="center"
                            direction="row"
                            spacing={1}>

                            <ArrowBackOutlined
                                sx={{
                                    transform: openMenu ? "rotate(180deg)" : "rotate(0deg)",
                                    transition: "transform 0.3s ease",
                                }}
                            />

                            <NullCheck valueForCheck={openMenu}>
                                <Typography variant="body2">
                                    {bt("CLOSE_MENU")}
                                </Typography>
                            </NullCheck>
                        </Stack>
                    </NullCheck>
                </Stack>

                <Box sx={{ height: "100%", overflowX: "hidden", scrollbarWidth: "none" }}>
                    <List sx={{ p: 0 }}>
                        {list?.map((btn, index) => {
                            return btn?.icon === "Divider"
                                ? <Stack key={index} paddingY={1}>
                                    <Divider>
                                        {openMenu && btn?.title !== "" &&
                                            <Typography variant="subtitle2" fontWeight="bold">
                                                {bt(btn?.title)}
                                            </Typography>
                                        }
                                    </Divider>
                                </Stack>
                                : <Fragment key={index}>
                                    <ListItemButton
                                        dir={lang?.dir}
                                        sx={{
                                            borderLeft: locationPage === btn.path && openMenu ? 4 : 0,
                                            borderColor: "primary.main",
                                            borderRadius: 1,
                                            height: 35,
                                            my: 0.5,
                                            "&:hover": { backgroundColor: "transparent !important", },
                                            "&.Mui-selected": {
                                                backgroundColor: "transparent !important",
                                                "&:hover": { backgroundColor: "transparent !important" },
                                            },
                                        }}
                                        onClick={() => onClick(btn)}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                "& .MuiSvgIcon-root": { color: locationPage === btn.path ? "primary.main" : "primary.text" }
                                            }}>
                                            {btn.icon}
                                        </ListItemIcon>
                                        {openMenu
                                            ? <Typography color={locationPage === btn.path ? "primary.main" : "primary.text"}>
                                                {bt(btn.title)}
                                            </Typography>
                                            : null
                                        }
                                    </ListItemButton>
                                </Fragment>
                        })}
                    </List>
                </Box>

                <NullCheck valueForCheck={openMenu}>
                    <Footer />
                </NullCheck>
            </Drawer >

            <Dialog open={openProfile} setOpen={setOpenProfile} />

        </>
    );
};
export default Menu;
